import { useCallback, useEffect, useRef, useState } from 'react'
import { Breadcrumbs } from '@/components/ui/breadcrumb'
import { CompositeFilterDescriptor } from '@progress/kendo-data-query'
import {
  GridColumn as Column,
  Grid,
  GridFilterChangeEvent,
  GridPageChangeEvent,
} from '@progress/kendo-react-grid'
import { Pagination } from 'api/Pagination/Types/Pagination'
import { WalletConfigDto } from 'api/Wallet/Types/WalletConfigDto'
import { Loading } from 'components/Loading'
import { Spinner } from 'components/Spinner'
import { pageableSettings } from 'store/grid/grid.slice'
import { useApiClient } from 'utils/api/ApiClient'
import { WalletsApiClient } from 'utils/api/walletsApiClient'
import { links } from 'utils/featureToggles'
import { getPaginationInitialState } from 'utils/paginationInitialState'
import { buildQuery } from 'utils/QueryHelpers'

import {
  AdministrationCountriesNavigation,
  AdministrationCountriesTab,
} from '../Countries/AdministrationCountriesNavigation'
import { normalizeWalletsSearchQuery } from './normalizer'
import walletColumns from './walletColumns'

const useFilter = () => {
  const walletsApiClient = useApiClient(WalletsApiClient)
  const isFirstRun = useRef(true)
  const [wallets, setWallets] = useState<WalletConfigDto[]>([])
  const [filter, setFilter] = useState<CompositeFilterDescriptor>({ logic: 'and', filters: [] })
  const [paginationState, setPaginationState] = useState<{ skip: number; take: number }>(
    getPaginationInitialState()
  )
  const [totalResults, setTotalResults] = useState<number>(0)
  const [isLoadingFilter, setLoading] = useState<boolean>(false)

  const getSearchQuery = useCallback((filterDescriptor: CompositeFilterDescriptor) => {
    return buildQuery(normalizeWalletsSearchQuery(filterDescriptor))
  }, [])

  const fetchFilter = async () => {
    setLoading(true)
    const searchQuery = getSearchQuery(filter)
    const pageIndex = 1
    const pageSize = paginationState.take

    const response = await walletsApiClient.getWalletsConfig({
      search: searchQuery,
      pageIndex,
      pageSize,
    })

    if (response?.items) {
      setWallets(response.items)
      setPaginationState({
        skip: 0,
        take: pageSize,
      })
      setTotalResults(response.totalResults)
    }
    setLoading(false)
  }

  useEffect(() => {
    if (isFirstRun.current) {
      isFirstRun.current = false
      return
    }

    const timeoutId = setTimeout(() => fetchFilter(), 500)
    return () => clearTimeout(timeoutId)
  }, [filter.filters]) // eslint-disable-line react-hooks/exhaustive-deps

  return {
    isLoadingFilter,
    filter,
    setFilter,
    paginationState,
    setPaginationState,
    setTotalResults,
    totalResults,
    wallets,
    setWallets,
    fetchFilter,
  }
}

const AdministrationWallets = (): JSX.Element => {
  const walletsApiClient = useApiClient(WalletsApiClient)

  const [isLoading, setLoading] = useState<boolean>(true)

  const {
    filter,
    setFilter,
    paginationState,
    setPaginationState,
    setTotalResults,
    totalResults,
    wallets,
    setWallets,
  } = useFilter()

  const getQuery = useCallback((filterDescriptor: CompositeFilterDescriptor) => {
    return buildQuery(normalizeWalletsSearchQuery(filterDescriptor))
  }, [])

  useEffect(() => {
    ;(async () => {
      setLoading(true)
      await fetchWallets()
      setLoading(false)
    })()
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const fetchWallets = async () => {
    const pagination = new Pagination({
      skip: paginationState.skip,
      recordsPerPage: paginationState.take,
    })
    const response = await walletsApiClient.getWalletsConfig({
      pageIndex: pagination.getPageIndex(),
      pageSize: pagination.getRecordsPerPage(),
    })
    if (response?.items) {
      setWallets(response.items)
      setPaginationState(paginationState)
      setTotalResults(response.totalResults)
    }
  }

  const handlePageChange = async (event: GridPageChangeEvent) => {
    const { skip, take } = event.page
    const pagination = new Pagination({
      skip,
      recordsPerPage: take,
    })

    const query = getQuery(filter)
    const pageIndex = !event.nativeEvent ? 1 : pagination.getPageIndex()
    const pageSize = !event.nativeEvent ? take : pagination.getRecordsPerPage()

    const usersResponse = await walletsApiClient.getWalletsConfig({
      search: query,
      pageIndex,
      pageSize,
    })
    if (usersResponse?.items) {
      setWallets(usersResponse.items)
    }
    setPaginationState({
      ...paginationState,
      take: pagination.getRecordsPerPage(),
      skip,
    })
  }

  const handleFilterChange = async (event: GridFilterChangeEvent) => {
    setFilter({
      ...event.filter,
      filters: event?.filter?.filters || [],
    })
  }

  if (isLoading) {
    return <Spinner />
  }

  return (
    <>
      <Breadcrumbs crumbs={[{ title: 'Wallets', link: links.administrationWallets.url }]} />
      <AdministrationCountriesNavigation tab={AdministrationCountriesTab.WALLETS} />
      <Loading isLoading={isLoading}>
        <Grid
          filterable
          data={wallets}
          filter={filter}
          take={paginationState.take}
          skip={paginationState.skip}
          total={totalResults}
          onPageChange={handlePageChange}
          onFilterChange={handleFilterChange}
          pageable={pageableSettings}
          sortable={{
            allowUnsort: true,
            mode: 'multiple',
          }}
          style={{ zIndex: 0 }}
        >
          {walletColumns
            .filter((c) => c.show !== false)
            .map((column, idx) => (
              <Column key={idx} {...column} />
            ))}
        </Grid>
      </Loading>
    </>
  )
}

export default AdministrationWallets
